import * as React from "react";
import { Modal, Spin, Typography, message } from "antd";
import {
  applyBodyParagraphWordChanges,
  analysisRowKey,
  buildTargetTextFromWordChanges,
  checkAgreementDocumentSupport,
  fetchActiveAiAnalysisJob,
  fetchAgreementVersions,
  formatAiAnalysisJobProgress,
  getAgreementIdFromDocument,
  getAgreementVersionIdFromDocument,
  isAbortError,
  isAiAnalysisJobActive,
  loadAiAnalysisFromServer,
  parseAddInAnalysisResponse,
  postBulkAiChange,
  replaceBodyParagraphText,
  revealBodyParagraph,
  formatHostError,
  resolveParagraphSourceText,
  resolveWordChangesForApply,
  retryParagraphAiAnalysis,
  startAiAnalysisJob,
  sendCurrentDocumentToIncoming,
  SEND_FOR_REVIEW_BLOCKED_UNSAVED_MESSAGE,
  isEmptyDocumentUploadError,
  sendCurrentDocumentToCleanCopy,
  CLEAN_COPY_BLOCKED_TRACKED_CHANGES_MESSAGE,
  CLEAN_COPY_BLOCKED_UNSAVED_MESSAGE,
  type AiChangeReviewStatus,
  type AnalysisParagraphResult,
  type VersionsResponse,
} from "../agreement";
import type { AuthUser } from "../types/auth";
import { VersionsPanel } from "./versions/VersionsPanel";
import { AiAnalysisPanel } from "./AiAnalysisPanel";
import { useAiAnalysisJobPolling } from "./aiAnalysis/hooks/useAiAnalysisJobPolling";
import { useLogout } from "../hooks/useAuth";
import { useAuthStore } from "../stores/useAuthStore";
import ChatPanel from "./ChatPanel";
import CollaboratePanel from "./CollaboratePanel";
import {
  PluginShell,
  type ShellTabKey,
} from "./shell/PluginShell";
import {
  mapAgreementVersionsForCollaborate,
  resolveDocumentComposerVersionId,
} from "../../collaborate/utils/mapAgreementVersionsForCollaborate";
import { useDocumentSelection } from "../hooks/useDocumentSelection";
import { setCollaborateApi } from "../../collaborate";
import {
  fetchInboxSummary,
} from "../../collaborate/api";
import { useCollaborateStore } from "../../collaborate/store/useCollaborateStore";
import type { InboxSummary } from "../../collaborate/types/collaborateThread";
import { createContractHubCollaborateApi } from "../api/collaborate";

/* Home (SummaryPage) and Changes (DiffPanel) remain in the codebase but are
 * unbound from navigation in Phase 1 — shell lands on Collaborate. */

const CENNA_APP_ORIGIN = "https://cenna.io";

const CENNA_PATHS = {
  createAgreement: "/",
  workspace: "/",
} as const;

export type TaskpaneAppProps = {
  title: string;
  user: AuthUser;
};

type TabKey = ShellTabKey;

/** ``checking`` = validating document; ``supported`` = stamped ids + version known to Contract Hub. */
type StampedAgreementSupport = "checking" | "supported" | "unsupported";

const TAB_COLLABORATE: TabKey = "collaborate";
const TAB_VERSION: TabKey = "versions";
const TAB_AI: TabKey = "analysis";
const TAB_ASK_CENNA: TabKey = "cenna";
const INBOX_SUMMARY_POLL_MS = 30_000;

/** `load` = GET saved analysis only; `run` = changes API + paragraph POSTs. */
type AiWorkPhase = "idle" | "load" | "run";

function _isLikelyNetworkFailure(message: string): boolean {
  const m = message.trim().toLowerCase();
  return (
    m.includes("load failed") ||
    m.includes("failed to fetch") ||
    m.includes("networkerror") ||
    m.includes("network error") ||
    m.includes("network request failed")
  );
}

function _extractHttpStatusCode(message: string): number | null {
  const m = message.match(/\b(\d{3})\b/);
  if (!m) {
    return null;
  }
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

function _buildGenericApiErrorMessage(err: unknown): string {
  const raw = (err instanceof Error ? err.message : String(err)).trim();
  const lower = raw.toLowerCase();

  if (_isLikelyNetworkFailure(raw) || (typeof navigator !== "undefined" && !navigator.onLine)) {
    return "We could not reach the server. Please check your internet connection and try again.";
  }

  if (lower.includes("timed out") || lower.includes("timeout")) {
    return "The request timed out. Please try again.";
  }

  const status = _extractHttpStatusCode(raw);
  if (status === 500) {
    return "Something went wrong on our server. Please try again in a moment.";
  }
  if (status === 502 || status === 503 || status === 504) {
    return "The server is temporarily unavailable. Please try again in a few minutes.";
  }
  if (status === 401 || status === 403) {
    return "Your session may have expired or you do not have access. Please sign in again and retry.";
  }
  if (status === 429) {
    return "Too many requests right now. Please wait a moment and try again.";
  }

  return "Request failed. Please try again.";
}

function buildInitialRowStatus(paragraphs: AnalysisParagraphResult[]): Record<string, AiChangeReviewStatus> {
  const out: Record<string, AiChangeReviewStatus> = {};
  paragraphs.forEach((row, i) => {
    const key = analysisRowKey(row, i);
    if (!row.compliant && row.reviewStatus && row.reviewStatus !== "pending") {
      out[key] = row.reviewStatus;
    }
  });
  return out;
}

function mergeRowStatusAfterRetry(
  parsed: AnalysisParagraphResult[],
  prevStatus: Record<string, AiChangeReviewStatus>,
  prevParagraphs: AnalysisParagraphResult[] | null,
  retriedIndex: number
): Record<string, AiChangeReviewStatus> {
  const statusByParagraphIndex = new Map<number, AiChangeReviewStatus>();
  if (prevParagraphs) {
    prevParagraphs.forEach((p, i) => {
      const k = analysisRowKey(p, i);
      const s = prevStatus[k];
      if (s && s !== "pending") {
        statusByParagraphIndex.set(p.index, s);
      }
    });
  }
  const out: Record<string, AiChangeReviewStatus> = {};
  parsed.forEach((row, i) => {
    const key = analysisRowKey(row, i);
    if (row.index === retriedIndex) {
      // Retry replaces analysis for this paragraph — do not carry over a prior accept.
      if (!row.compliant && row.reviewStatus === "rejected") {
        out[key] = "rejected";
      }
      return;
    }
    const preserved = statusByParagraphIndex.get(row.index);
    if (preserved) {
      out[key] = preserved;
    } else if (!row.compliant && row.reviewStatus && row.reviewStatus !== "pending") {
      out[key] = row.reviewStatus;
    }
  });
  return out;
}

function countAnalysisIssues(paragraphs: AnalysisParagraphResult[] | null | undefined): number {
  return paragraphs?.filter((row) => !row.compliant).length ?? 0;
}

function formatNavVersionBadge(versionsData: VersionsResponse | null): string {
  const display = versionsData?.display_label?.trim();
  if (display) {
    return display.length > 8 ? `${display.slice(0, 7)}…` : display;
  }
  const current = versionsData?.current_version?.trim();
  if (!current) {
    return "—";
  }
  const numeric = /^(\d+)/.exec(current);
  if (numeric) {
    return `v${numeric[1]}`;
  }
  return current.length > 8 ? `${current.slice(0, 7)}…` : current;
}

const TaskpaneApp: React.FC<TaskpaneAppProps> = (props) => {
  const { mutate: logout, isPending: logoutPending } = useLogout();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [stampedSupport, setStampedSupport] = React.useState<StampedAgreementSupport>("checking");
  const [tab, setTab] = React.useState<TabKey>(TAB_COLLABORATE);
  const [inboxSummary, setInboxSummary] = React.useState<InboxSummary | null>(null);
  const [navAnalysisIssueCount, setNavAnalysisIssueCount] = React.useState(0);
  const [documentSubtitle, setDocumentSubtitle] = React.useState<string>("—");

  const documentSelectionEnabled = stampedSupport === "supported";
  const { selectedContext, clearSelection } = useDocumentSelection({
    enabled: documentSelectionEnabled,
  });

  React.useEffect(() => {
    setCollaborateApi(createContractHubCollaborateApi());
  }, []);

  const refreshInboxSummary = React.useCallback(async (_options?: { showLoading?: boolean }) => {
    if (stampedSupport !== "supported") {
      return;
    }
    try {
      const contractId = await getAgreementIdFromDocument();
      if (!contractId?.trim()) {
        setInboxSummary(null);
        return;
      }
      const summary = await fetchInboxSummary(contractId);
      setInboxSummary(summary);
    } catch {
      setInboxSummary(null);
    }
  }, [stampedSupport]);

  React.useEffect(() => {
    if (stampedSupport !== "supported") {
      setInboxSummary(null);
      return undefined;
    }
    void refreshInboxSummary();
    const intervalId = window.setInterval(() => {
      void refreshInboxSummary();
    }, INBOX_SUMMARY_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [stampedSupport, refreshInboxSummary]);

  const prevTabRef = React.useRef(tab);
  React.useEffect(() => {
    if (prevTabRef.current === TAB_COLLABORATE && tab !== TAB_COLLABORATE) {
      void refreshInboxSummary();
    }
    prevTabRef.current = tab;
  }, [tab, refreshInboxSummary]);

  React.useEffect(() => {
    let cancelled = false;
    try {
      const url = Office.context?.document?.url;
      if (typeof url === "string" && url.trim()) {
        const cleaned = url.split(/[\\/]/).pop() || url;
        const withoutQuery = cleaned.split("?")[0] || cleaned;
        if (!cancelled && withoutQuery.trim()) {
          setDocumentSubtitle(withoutQuery.trim());
        }
      }
    } catch {
      /* Office context may be unavailable in some hosts */
    }
    return () => {
      cancelled = true;
    };
  }, []);
  const activeCollaborateThreadId = useCollaborateStore((s) => s.activeThreadId);
  const prevActiveThreadRef = React.useRef<string | null>(activeCollaborateThreadId);
  React.useEffect(() => {
    const prev = prevActiveThreadRef.current;
    if (tab === TAB_COLLABORATE && prev && !activeCollaborateThreadId) {
      void refreshInboxSummary();
    }
    prevActiveThreadRef.current = activeCollaborateThreadId;
  }, [activeCollaborateThreadId, tab, refreshInboxSummary]);

  const prevCollaborateStoreRef = React.useRef(useCollaborateStore.getState());
  React.useEffect(() => {
    return useCollaborateStore.subscribe((state) => {
      const prevState = prevCollaborateStoreRef.current;
      for (const threadId of Object.keys(state.threadsById)) {
        if (prevState.threadsById[threadId]?.unread && !state.threadsById[threadId]?.unread) {
          setInboxSummary((current) =>
            current
              ? {
                  ...current,
                  unreadMessages: Math.max(0, current.unreadMessages - 1),
                }
              : current,
          );
        }
      }
      prevCollaborateStoreRef.current = state;
    });
  }, []);

  const pendingFocusBootstrapRef = React.useRef(false);

  React.useEffect(() => {
    if (stampedSupport !== "supported" || pendingFocusBootstrapRef.current) {
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        const versionId = await getAgreementVersionIdFromDocument();
        if (cancelled || !versionId?.trim()) {
          return;
        }
        pendingFocusBootstrapRef.current = true;
        setDocumentVersionId(versionId);
        // Do not auto-switch to Collaborate on load. Pending focus (email deep links)
        // is applied when the user opens the Collaborate tab.
      } catch {
        /* version bootstrap is best-effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stampedSupport]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await checkAgreementDocumentSupport();
      if (cancelled) {
        return;
      }
      setStampedSupport(result === "supported" ? "supported" : "unsupported");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const [versionsData, setVersionsData] = React.useState<VersionsResponse | null>(null);
  const [versionsStatus, setVersionsStatus] = React.useState("");
  const [versionsError, setVersionsError] = React.useState("");
  const [versionsLoading, setVersionsLoading] = React.useState(false);
  const [documentVersionId, setDocumentVersionId] = React.useState<string | null>(null);

  const [incomingStatus, setIncomingStatus] = React.useState("");
  const [incomingError, setIncomingError] = React.useState("");
  const [incomingLoading, setIncomingLoading] = React.useState(false);
  const [cleanCopyStatus, setCleanCopyStatus] = React.useState("");
  const [cleanCopyError, setCleanCopyError] = React.useState("");
  const [cleanCopyLoading, setCleanCopyLoading] = React.useState(false);

  const [aiStatus, setAiStatus] = React.useState("");
  const [aiPhase, setAiPhase] = React.useState<AiWorkPhase>("idle");
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiApplyError, setAiApplyError] = React.useState("");
  const [aiParagraphs, setAiParagraphs] = React.useState<AnalysisParagraphResult[] | null>(null);
  const [aiParagraphSnapshot, setAiParagraphSnapshot] = React.useState<Record<number, string> | null>(null);
  const [aiRowStatus, setAiRowStatus] = React.useState<Record<string, "pending" | "accepted" | "rejected">>({});
  const [aiApplyingKey, setAiApplyingKey] = React.useState<string | null>(null);
  const [aiRetryingIndex, setAiRetryingIndex] = React.useState<number | null>(null);
  const [aiRetryError, setAiRetryError] = React.useState("");
  const [aiRetryErrorIndex, setAiRetryErrorIndex] = React.useState<number | null>(null);
  const aiParagraphsRef = React.useRef<AnalysisParagraphResult[] | null>(null);

  /** When true, opening the AI tab from Home will run analysis if saved results are empty. */
  const autoRunAiIfEmptyFromHomeRef = React.useRef(false);
  const aiRunAbortRef = React.useRef<AbortController | null>(null);
  /** Aborts in-flight tab "load saved analysis" when a Run starts (or tab leaves). */
  const aiLoadAbortRef = React.useRef<AbortController | null>(null);
  /** True while a background AI analysis job is being polled. */
  const aiParagraphRunActiveRef = React.useRef(false);
  const onRunAiAnalysisRef = React.useRef<() => Promise<void>>(async () => undefined);
  const { startPolling, stopPolling } = useAiAnalysisJobPolling();

  const hydrateAiAnalysisFromServer = React.useCallback(async (signal?: AbortSignal) => {
    const { raw, paragraphTextByIndex } = await loadAiAnalysisFromServer(undefined, signal);
    if (signal?.aborted) {
      return null;
    }
    const parsed = parseAddInAnalysisResponse(raw);
    setAiParagraphSnapshot(paragraphTextByIndex);
    setAiParagraphs(parsed);
    setAiRowStatus(buildInitialRowStatus(parsed));
    setNavAnalysisIssueCount(countAnalysisIssues(parsed));
    return parsed;
  }, []);

  const attachAiJobPoller = React.useCallback(
    (jobId: string) => {
      aiParagraphRunActiveRef.current = true;
      setAiPhase("run");
      setAiLoading(true);
      startPolling(jobId, {
        onTick: async (job) => {
          setAiStatus(formatAiAnalysisJobProgress(job));
          try {
            await hydrateAiAnalysisFromServer();
          } catch {
            /* partial hydrate is best-effort while the job runs */
          }
        },
        onTerminal: async (job) => {
          aiParagraphRunActiveRef.current = false;
          try {
            await hydrateAiAnalysisFromServer();
          } catch {
            /* final hydrate best-effort */
          }
          if (job.status === "failed") {
            setAiStatus("");
            setAiLoading(false);
            setAiPhase("idle");
            Modal.error({
              title: "AI analysis failed",
              content: job.errorMessage?.trim() || _buildGenericApiErrorMessage(new Error("AI analysis failed")),
              okText: "OK",
            });
            return;
          }
          if (job.status === "cancelled") {
            setAiStatus("");
            setAiLoading(false);
            setAiPhase("idle");
            return;
          }
          setAiStatus("");
          setAiLoading(false);
          setAiPhase("idle");
        },
        onError: (err) => {
          aiParagraphRunActiveRef.current = false;
          setAiStatus("");
          setAiLoading(false);
          setAiPhase("idle");
          Modal.error({
            title: "Request failed",
            content: _buildGenericApiErrorMessage(err),
            okText: "OK",
          });
        },
      });
    },
    [hydrateAiAnalysisFromServer, startPolling]
  );

  const loadVersions = React.useCallback(async (options?: { preservePrevious?: boolean }) => {
    setVersionsError("");
    setVersionsLoading(true);
    setVersionsStatus("Loading versions…");
    if (!options?.preservePrevious) {
      setVersionsData(null);
    }
    try {
      const data = await fetchAgreementVersions(setVersionsStatus);
      setVersionsData(data);
      setVersionsStatus("");
    } catch (err) {
      setVersionsStatus("");
      setVersionsError(_formatReactError(err));
      if (!options?.preservePrevious) {
        setVersionsData(null);
      }
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  const refreshNavAnalysisCount = React.useCallback(async (signal?: AbortSignal) => {
    try {
      const { raw, paragraphTextByIndex } = await loadAiAnalysisFromServer(undefined, signal);
      if (signal?.aborted || aiParagraphRunActiveRef.current) {
        return null;
      }
      const parsed = parseAddInAnalysisResponse(raw);
      setNavAnalysisIssueCount(countAnalysisIssues(parsed));
      if (aiParagraphsRef.current == null && !aiParagraphRunActiveRef.current) {
        setAiParagraphSnapshot(paragraphTextByIndex);
        setAiParagraphs(parsed);
        setAiRowStatus(buildInitialRowStatus(parsed));
      }
      return parsed;
    } catch {
      return null;
    }
  }, []);

  React.useEffect(() => {
    if (stampedSupport !== "supported") {
      return undefined;
    }
    const ac = new AbortController();
    void loadVersions({ preservePrevious: true });
    void refreshNavAnalysisCount(ac.signal);
    return () => ac.abort();
  }, [stampedSupport, loadVersions, refreshNavAnalysisCount]);

  React.useEffect(() => {
    if (tab !== TAB_VERSION) {
      return;
    }
    void loadVersions({ preservePrevious: true });
  }, [tab, loadVersions]);

  React.useEffect(() => {
    if (tab !== TAB_COLLABORATE) {
      return;
    }
    void loadVersions({ preservePrevious: true });
    void getAgreementVersionIdFromDocument()
      .then((versionId) => setDocumentVersionId(versionId))
      .catch(() => setDocumentVersionId(null));
  }, [tab, loadVersions]);

  const collaborateVersions = React.useMemo(
    () => mapAgreementVersionsForCollaborate(versionsData),
    [versionsData],
  );

  const defaultComposerVersionId = React.useMemo(
    () =>
      resolveDocumentComposerVersionId(
        collaborateVersions,
        documentVersionId,
        versionsData?.current_version ?? null,
      ),
    [collaborateVersions, documentVersionId, versionsData?.current_version],
  );

  const onRunAiAnalysis = React.useCallback(async () => {
    // Prevent double-starts while a job is already running (matches web UI Re-run busy state).
    if (aiParagraphRunActiveRef.current) {
      return;
    }
    // Cancel any in-flight "load saved analysis" and prior poller.
    aiLoadAbortRef.current?.abort();
    aiRunAbortRef.current?.abort();
    stopPolling();
    const ac = new AbortController();
    aiRunAbortRef.current = ac;
    aiParagraphRunActiveRef.current = true;

    setAiPhase("run");
    setAiLoading(true);
    setAiApplyError("");
    setAiParagraphs(null);
    setAiParagraphSnapshot(null);
    setAiRowStatus({});
    setAiStatus("Starting…");
    try {
      const job = await startAiAnalysisJob(undefined, ac.signal);
      if (ac.signal.aborted) {
        return;
      }
      if (job.noChanges || !job.jobId) {
        aiParagraphRunActiveRef.current = false;
        setAiParagraphs([]);
        setAiRowStatus({});
        setNavAnalysisIssueCount(0);
        setAiStatus("No paragraph changes found to analyse for this version.");
        setAiLoading(false);
        setAiPhase("idle");
        Modal.info({
          title: "No changes to analyse",
          content:
            "This version has no paragraph changes for AI analysis. Upload a revised document with tracked edits, then try again.",
          okText: "OK",
        });
        return;
      }
      setAiStatus(formatAiAnalysisJobProgress(job));
      attachAiJobPoller(job.jobId);
    } catch (err) {
      aiParagraphRunActiveRef.current = false;
      if (isAbortError(err)) {
        setAiStatus("");
        setAiLoading(false);
        setAiPhase("idle");
        return;
      }
      setAiStatus("");
      setAiLoading(false);
      setAiPhase("idle");
      Modal.error({
        title: "Request failed",
        content: _buildGenericApiErrorMessage(err),
        okText: "OK",
      });
    }
  }, [attachAiJobPoller, stopPolling]);

  onRunAiAnalysisRef.current = onRunAiAnalysis;

  const resumeAiJobIfActive = React.useCallback(
    async (signal?: AbortSignal): Promise<boolean> => {
      try {
        const active = await fetchActiveAiAnalysisJob(undefined, signal);
        if (signal?.aborted || !active?.jobId || !isAiAnalysisJobActive(active.status)) {
          return false;
        }
        setAiStatus(formatAiAnalysisJobProgress(active));
        attachAiJobPoller(active.jobId);
        return true;
      } catch (err) {
        if (isAbortError(err) || signal?.aborted) {
          return false;
        }
        // Non-fatal: treat as no active job.
        return false;
      }
    },
    [attachAiJobPoller]
  );

  React.useEffect(() => {
    if (tab !== TAB_AI) {
      autoRunAiIfEmptyFromHomeRef.current = false;
      if (!aiParagraphRunActiveRef.current) {
        aiRunAbortRef.current?.abort();
        aiLoadAbortRef.current?.abort();
        setAiPhase("idle");
      }
      return undefined;
    }

    if (aiParagraphRunActiveRef.current) {
      return undefined;
    }

    const ac = new AbortController();
    aiLoadAbortRef.current = ac;
    let cancelled = false;
    const hasExisting = aiParagraphsRef.current != null;
    setAiPhase("load");
    setAiLoading(true);
    setAiApplyError("");
    if (!hasExisting) {
      setAiParagraphs(null);
      setAiParagraphSnapshot(null);
      setAiRowStatus({});
      setAiStatus("Checking for saved AI analysis…");
    } else {
      setAiStatus("");
    }
    void (async () => {
      try {
        const { raw, paragraphTextByIndex } = await loadAiAnalysisFromServer(setAiStatus, ac.signal);
        if (cancelled || aiParagraphRunActiveRef.current) {
          return;
        }
        const parsed = parseAddInAnalysisResponse(raw);
        setAiParagraphSnapshot(paragraphTextByIndex);
        setAiParagraphs(parsed);
        setAiRowStatus(buildInitialRowStatus(parsed));
        setNavAnalysisIssueCount(countAnalysisIssues(parsed));

        const resumed = await resumeAiJobIfActive(ac.signal);
        if (cancelled || resumed || aiParagraphRunActiveRef.current) {
          return;
        }

        setAiStatus("");
        const shouldAutoRun =
          autoRunAiIfEmptyFromHomeRef.current && parsed.length === 0;
        autoRunAiIfEmptyFromHomeRef.current = false;
        if (shouldAutoRun) {
          await onRunAiAnalysisRef.current();
        } else {
          setAiPhase("idle");
        }
      } catch (err) {
        if (cancelled || isAbortError(err) || aiParagraphRunActiveRef.current) {
          if (!aiParagraphRunActiveRef.current) {
            setAiPhase("idle");
          }
          return;
        }
        autoRunAiIfEmptyFromHomeRef.current = false;
        setAiStatus("");
        setAiPhase("idle");
        Modal.error({
          title: "Request failed",
          content: _buildGenericApiErrorMessage(err),
          okText: "OK",
        });
        setAiParagraphs(null);
        setAiParagraphSnapshot(null);
      } finally {
        // Never clear loading while a job poll owns the UI.
        if (!cancelled && !aiParagraphRunActiveRef.current) {
          setAiLoading(false);
        }
        if (cancelled && !aiParagraphRunActiveRef.current) {
          setAiPhase("idle");
        }
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
      if (aiLoadAbortRef.current === ac) {
        aiLoadAbortRef.current = null;
      }
    };
  }, [tab, resumeAiJobIfActive]);

  const openCenna = (path: string) => {
    const url = `${CENNA_APP_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const onSendIncoming = async () => {
    setIncomingError("");
    setIncomingStatus("");
    setIncomingLoading(true);
    try {
      const result = await sendCurrentDocumentToIncoming(setIncomingStatus);
      if (result === "blocked_unsaved") {
        setIncomingStatus("");
        Modal.warning({
          title: "Save document first",
          content: SEND_FOR_REVIEW_BLOCKED_UNSAVED_MESSAGE,
          okText: "OK",
        });
      } else {
        setIncomingStatus("");
        Modal.success({
          title: "Send for review",
          content: result.message,
          okText: "OK",
        });
      }
    } catch (err) {
      setIncomingStatus("");
      if (isEmptyDocumentUploadError(err)) {
        Modal.warning({
          title: "Cannot upload document",
          content:
            "This document has no content to upload. Add text to the document and then try again.",
          okText: "OK",
        });
      } else {
        setIncomingError(_formatReactError(err));
      }
    } finally {
      setIncomingLoading(false);
    }
  };

  const onSendCleanCopy = async () => {
    setCleanCopyError("");
    setCleanCopyStatus("");
    setCleanCopyLoading(true);
    try {
      const result = await sendCurrentDocumentToCleanCopy(setCleanCopyStatus);
      if (result === "blocked_tracked_changes") {
        setCleanCopyStatus("");
        Modal.warning({
          title: "Finish tracked changes first",
          content: CLEAN_COPY_BLOCKED_TRACKED_CHANGES_MESSAGE,
          okText: "Got it",
        });
      } else if (result === "blocked_unsaved") {
        setCleanCopyStatus("");
        Modal.warning({
          title: "Save in Word, then retry",
          content: CLEAN_COPY_BLOCKED_UNSAVED_MESSAGE,
          okText: "Got it",
        });
      } else {
        setCleanCopyStatus("");
        Modal.success({
          title: "Clean copy",
          content: result.message,
          okText: "OK",
        });
      }
    } catch (err) {
      setCleanCopyStatus("");
      if (isEmptyDocumentUploadError(err)) {
        Modal.warning({
          title: "Cannot upload document",
          content:
            "This document has no content to upload. Add text to the document and then try again.",
          okText: "OK",
        });
      } else {
        setCleanCopyError(_formatReactError(err));
      }
    } finally {
      setCleanCopyLoading(false);
    }
  };

  const refreshAiAnalysisFromServer = React.useCallback(async () => {
    const { raw, paragraphTextByIndex } = await loadAiAnalysisFromServer();
    const parsed = parseAddInAnalysisResponse(raw);
    setAiParagraphSnapshot(paragraphTextByIndex);
    setAiParagraphs(parsed);
    setAiRowStatus(buildInitialRowStatus(parsed));
    setNavAnalysisIssueCount(countAnalysisIssues(parsed));
  }, []);

  React.useEffect(() => {
    aiParagraphsRef.current = aiParagraphs;
  }, [aiParagraphs]);

  const onRetryParagraph = React.useCallback(
    async (row: AnalysisParagraphResult, _key: string) => {
      setAiApplyError("");
      setAiRetryError("");
      setAiRetryErrorIndex(null);
      setAiRetryingIndex(row.index);
      try {
        const { raw, paragraphTextByIndex } = await retryParagraphAiAnalysis(row.index);
        const parsed = parseAddInAnalysisResponse(raw);
        setAiParagraphSnapshot(paragraphTextByIndex);
        setAiParagraphs((prev) => {
          setAiRowStatus((prevStatus) =>
            mergeRowStatusAfterRetry(parsed, prevStatus, prev, row.index)
          );
          return parsed;
        });
      } catch (err) {
        setAiRetryError(_buildGenericApiErrorMessage(err));
        setAiRetryErrorIndex(row.index);
        try {
          await refreshAiAnalysisFromServer();
        } catch {
          /* best-effort restore */
        }
      } finally {
        setAiRetryingIndex(null);
      }
    },
    [refreshAiAnalysisFromServer]
  );

  const onClearRetryError = React.useCallback(() => {
    setAiRetryError("");
    setAiRetryErrorIndex(null);
  }, []);

  const onRevealIssueInDocument = React.useCallback(
    async (row: AnalysisParagraphResult) => {
      try {
        const expectedText = resolveParagraphSourceText(row, aiParagraphSnapshot);
        await revealBodyParagraph(row.index, { expectedText });
      } catch (err) {
        message.warning(formatHostError(err));
      }
    },
    [aiParagraphSnapshot]
  );

  const onAcceptSuggestion = async (
    row: AnalysisParagraphResult,
    key: string,
    options?: { revealSelection?: boolean; overrideText?: string; skipRefresh?: boolean }
  ) => {
    setAiApplyError("");
    setAiApplyingKey(key);
    try {
      const expectedText = resolveParagraphSourceText(row, aiParagraphSnapshot);
      const targetText =
        options?.overrideText !== undefined
          ? options.overrideText
          : row.suggestion.trim().length > 0
            ? row.suggestion
            : buildTargetTextFromWordChanges(row.wordChanges ?? []);
      const revealSelection = options?.revealSelection !== false;
      const priorStatus = aiRowStatus[key] ?? row.reviewStatus ?? "pending";
      const isReapply = priorStatus === "accepted" && options?.overrideText === undefined;
      const applyOptions =
        options?.overrideText !== undefined
          ? options
          : isReapply
            ? { ...options, reapply: true as const }
            : options;
      const wordChangesToApply = resolveWordChangesForApply(
        row,
        targetText,
        expectedText,
        applyOptions
      );
      try {
        if (wordChangesToApply) {
          await applyBodyParagraphWordChanges(row.index, targetText, wordChangesToApply, {
            expectedText,
            revealSelection,
          });
        } else {
          await replaceBodyParagraphText(row.index, targetText, { expectedText, revealSelection });
        }
      } catch (applyErr) {
        if (wordChangesToApply) {
          await replaceBodyParagraphText(row.index, targetText, { expectedText, revealSelection });
        } else {
          throw applyErr;
        }
      }
      setAiRowStatus((prev) => ({ ...prev, [key]: "accepted" }));
      if (row.id) {
        setAiParagraphs((prev) =>
          prev?.map((p) =>
            p.id === row.id
              ? {
                  ...p,
                  suggestion: targetText,
                  ...(wordChangesToApply ? { wordChanges: wordChangesToApply } : { wordChanges: undefined }),
                  reviewStatus: "accepted" as const,
                }
              : p
          ) ?? null
        );
      }
      if (row.id) {
        try {
          const versionId = await getAgreementVersionIdFromDocument();
          const acceptedSuggestions =
            options?.overrideText !== undefined
              ? [{ id: row.id, suggestion: targetText }]
              : undefined;
          await postBulkAiChange(versionId, [row.id], [], acceptedSuggestions);
          if (!options?.skipRefresh) {
            try {
              await refreshAiAnalysisFromServer();
            } catch {
              // best-effort refresh
            }
          }
        } catch {
          // best-effort: don't block the UI if the server call fails
        }
      }
    } catch (err) {
      setAiApplyError(formatHostError(err));
    } finally {
      setAiApplyingKey(null);
    }
  };

  const onRejectSuggestion = async (
    row: AnalysisParagraphResult,
    key: string,
    options?: { skipRefresh?: boolean }
  ) => {
    setAiRowStatus((prev) => ({ ...prev, [key]: "rejected" }));
    if (row.id) {
      try {
        const versionId = await getAgreementVersionIdFromDocument();
        await postBulkAiChange(versionId, [], [row.id]);
        if (!options?.skipRefresh) {
          try {
            await refreshAiAnalysisFromServer();
          } catch {
            // best-effort refresh
          }
        }
      } catch {
        // best-effort
      }
    }
  };

  const userName = props.user.name || undefined;
  const userEmail = props.user.email || undefined;
  const collaborateUnreadCount = inboxSummary?.unreadMessages ?? 0;
  const navVersionLabel = React.useMemo(() => formatNavVersionBadge(versionsData), [versionsData]);

  const unsupportedMessage = "This is not a supported contract file.";

  const shellTabs = React.useMemo(
    () => [
      {
        id: TAB_COLLABORATE as ShellTabKey,
        label: "Collaborate",
        badge: collaborateUnreadCount,
        alwaysShowBadge: true,
        badgeAriaLabel: `${collaborateUnreadCount} unread`,
      },
      {
        id: TAB_AI as ShellTabKey,
        label: "Analysis",
        badge: navAnalysisIssueCount,
        alwaysShowBadge: true,
        badgeAriaLabel: `${navAnalysisIssueCount} issues`,
      },
      { id: TAB_ASK_CENNA as ShellTabKey, label: "Ask Cenna", showCennaMark: true },
      {
        id: TAB_VERSION as ShellTabKey,
        label: "Versions",
        badge: navVersionLabel,
        alwaysShowBadge: true,
        badgeAriaLabel: `Current version ${navVersionLabel}`,
      },
    ],
    [collaborateUnreadCount, navAnalysisIssueCount, navVersionLabel],
  );

  const uploadOverlayTip = incomingLoading
    ? incomingStatus || "Sending to the platform…"
    : cleanCopyLoading
      ? cleanCopyStatus || "Generating clean copy…"
      : null;

  const actionError = incomingError || cleanCopyError || undefined;

  const renderTabContent = () => {
    if (stampedSupport === "checking") {
      return (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 120,
          }}
        >
          <Spin size="large" />
        </div>
      );
    }
    if (stampedSupport !== "supported") {
      return (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px 8px",
            textAlign: "center",
          }}
        >
          <Typography.Paragraph style={{ marginBottom: 0, fontSize: 14 }}>
            {unsupportedMessage}
          </Typography.Paragraph>
        </div>
      );
    }

    switch (tab) {
      case TAB_COLLABORATE:
        return (
          <CollaboratePanel
            active={tab === TAB_COLLABORATE}
            selectedContext={
              selectedContext?.text ? { quote: selectedContext.text } : null
            }
            onSelectionConsumed={() => void clearSelection()}
            versions={collaborateVersions}
            defaultComposerVersionId={defaultComposerVersionId}
            currentVersionId={defaultComposerVersionId}
            inboxSummary={inboxSummary}
            accessToken={accessToken}
          />
        );
      case TAB_AI:
        return (
          <AiAnalysisPanel
            aiPhase={aiPhase}
            aiStatus={aiStatus}
            aiApplyError={aiApplyError}
            aiParagraphs={aiParagraphs}
            aiLoading={aiLoading}
            aiRowStatus={aiRowStatus}
            aiApplyingKey={aiApplyingKey}
            aiRetryingIndex={aiRetryingIndex}
            aiRetryError={aiRetryError}
            aiRetryErrorIndex={aiRetryErrorIndex}
            onRunAiAnalysis={onRunAiAnalysis}
            onAcceptSuggestion={onAcceptSuggestion}
            onRejectSuggestion={onRejectSuggestion}
            onRetryParagraph={onRetryParagraph}
            onRefreshAiAnalysis={refreshAiAnalysisFromServer}
            onClearRetryError={onClearRetryError}
            onRevealIssueInDocument={onRevealIssueInDocument}
          />
        );
      case TAB_ASK_CENNA:
        return <ChatPanel active={tab === TAB_ASK_CENNA} />;
      case TAB_VERSION:
        return (
          <VersionsPanel
            versionsData={versionsData}
            versionsStatus={versionsStatus}
            versionsError={versionsError}
            loading={versionsLoading}
            onRefresh={() => void loadVersions()}
          />
        );
      default:
        return null;
    }
  };

  const documentSupported = stampedSupport === "supported";

  return (
    <PluginShell
      title={props.title}
      subtitle={documentSubtitle}
      userName={userName}
      userEmail={userEmail}
      logoutPending={logoutPending}
      onLogout={() => void logout()}
      onGoToPlatform={() => openCenna(CENNA_PATHS.workspace)}
      incomingLoading={incomingLoading}
      cleanCopyLoading={cleanCopyLoading}
      actionError={documentSupported ? actionError : undefined}
      onSaveAndUpload={documentSupported ? () => void onSendIncoming() : undefined}
      onCleanCopy={documentSupported ? () => void onSendCleanCopy() : undefined}
      activeTab={tab}
      onTabChange={setTab}
      tabs={shellTabs}
      uploadOverlayTip={documentSupported ? uploadOverlayTip : null}
      showWorkspaceChrome={documentSupported}
    >
      {renderTabContent()}
    </PluginShell>
  );
};

function _formatReactError(err: unknown): string {
  return _buildGenericApiErrorMessage(err);
}

export default TaskpaneApp;
